import { TabsStateService } from './../../../../../shared/services/tabs-state.service';
import { LocalDatePipe } from '@anihis/shared/src/pipes/local-date.pipe';
import { ImagePreviewDialogComponent } from './../../../../../shared/component/image-preview-dialog/image-preview-dialog.component';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatNativeDateModule } from '@angular/material/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { appInit } from '@anihis/shared/src/functions/appInitLang';
import { AddEditReportDialogComponent } from '../../../add-edit-report-dialog/add-edit-report-dialog.component';
import { ReportVisibilityDialogComponent } from './report-visibility-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FormBaseComponent } from '@anihis/shared/src/shared/base-components/form-base.component';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { ReportStore } from './report.store';
import { FormControl, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, take } from 'rxjs';
import { LoadingStatus } from '../../../../../shared/enums/loading';
import { PreviewCardHistoryComponent } from 'apps/anihis-portal/src/app/shared/component/preview-health-report-dialog/preview-health-report.component';
import { ConfirmationDialogComponent } from 'apps/anihis-portal/src/app/shared/component/confirmation-dialog/confirmation-dialog.component';
import { ShellStore } from 'apps/anihis-portal/src/app/shell/shell.store';
import {
  TranslocoService,
  TranslocoModule,
  translate,
} from '@jsverse/transloco';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { LoadingSpinnerComponent } from '../../../../../shared/component/loading-spinner/loading-spinner.component';
import { LoadingSkeletonComponent } from '../../../../../shared/component/loading-skeleton/loading-spinner.component';
import { LoadingOverlayComponent } from '../../../../../shared/component/loading-overlay/loading-overlay.component';
import { EmptyFallbackHtmlPipe } from '../../../../../shared/pipe/empty-fallback-html.pipe';
import { PORTAL_DIALOG } from '../../../../../shared/constants/dialog.constants';
import { AdminItemMobileCardComponent } from '../../../../../shared/component/admin-item-mobile-card/admin-item-mobile-card.component';
import { AuthenticationService } from '@anihis/shared/src/services/authentication.service';
import { SelectClinicStore } from '../../../../../shared/component/select-clinic/select-clinic.store';
import { HealthRecordsService } from '@anihis/core/data-access';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'anihis-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoModule,
    MatButtonModule,
    MatChipsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    MatNativeDateModule,
    InfiniteScrollDirective,
    LoadingSpinnerComponent,
    LoadingSkeletonComponent,
    LoadingOverlayComponent,
    EmptyFallbackHtmlPipe,
    AdminItemMobileCardComponent,
    LocalDatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ReportStore, { provide: MAT_DATE_LOCALE, useFactory: appInit }],
})
export class ReportComponent extends FormBaseComponent {
  reportUid = output();
  refreshReports = input<number>();
  animalUid = input<string>();

  private dialog = inject(MatDialog);
  store = inject(ReportStore);
  shellStore = inject(ShellStore);
  transloco = inject(TranslocoService);
  private authService = inject(AuthenticationService);
  private selectClinicStore = inject(SelectClinicStore);
  private tabsStateService = inject(TabsStateService);
  private cdr = inject(ChangeDetectorRef);
  private healthRecordsService = inject(HealthRecordsService);
  private snackBar = inject(MatSnackBar);
  sendingEmailUid = signal<string | null>(null);

  @ViewChild('tableWrapper', { static: false })
  tableWrapperElement!: ElementRef;

  readonly status = LoadingStatus;

  onScrollDown = signal(false);

  override form = this.fb.group({
    range: this.fb.group({
      start: [null],
      end: [null],
    }),
    search: this.fb.control<string>(''),
    veterinarian: this.fb.control<string>(''),
  });

  displayedColumns: string[] = [
    'Health Record Number',
    'Name Of Clinic',
    'License Number',
    'Creation Date',
    'Last Modified Date',
    'Actions',
  ];

  constructor() {
    super();

    explicitEffect([this.animalUid], ([animalUid]) => {
      if (!animalUid) return;
      this.store.setAnimalUid(animalUid!);
      this.store.fetchRecord().subscribe();
    });

    explicitEffect([this.refreshReports], ([refreshReports]) => {
      if (refreshReports) {
        this.store.fetchRecord().subscribe({
          next: () => {},
          error: (err) =>
            console.error('Error fetching reports after refresh', err),
        });
        this.cdr.markForCheck();
      }
    });

    this.form
      .get('search')
      ?.valueChanges.pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value) => {
        this.store.updateSearch(value!);
      });

    effect(() => {
      const index = this.tabsStateService.selectedIndex();
      const currentTab = this.tabsStateService.tabs()[index];
      if (currentTab === 'Cards' && this.tableWrapperElement) {
        this.tableWrapperElement.nativeElement.scrollTop = 0;
      }
    });

    // React na promenu veterinara
    this.veterinarian.valueChanges.subscribe((veterinarian) => {
      this.store.updateVeterinariansFilter(veterinarian);
    });

    // React na kompletiran date range
    this.range.valueChanges.subscribe(({ start, end }) => {
      if (start && end) {
        this.store.updateDateRangeFilter(start, end);
      }
    });
  }

  get range(): FormGroup {
    return this.form.get('range') as FormGroup;
  }

  get veterinarian(): FormControl<string> {
    return this.form.get('veterinarian') as FormControl<string>;
  }

  get hasReports(): boolean {
    const reports = this.store.reports();
    return Array.isArray(reports) && reports.length > 0;
  }

  clearRange() {
    this.range.reset();
    this.store.updateDateRangeFilter(undefined, undefined);
  }

  clearSearch() {
    this.form.get('search')?.setValue('');
  }

  loadMore() {
    if (
      this.store.status() !== this.status.Loading &&
      this.store.pagination()?.hasNextPage
    ) {
      this.store.loadMoreData().subscribe();
    }
  }

  openVisibilityDialog(data: any) {
    const healthRecordUid = data?.healthRecordUid;
    if (!healthRecordUid) return;

    const dialogRef = this.dialog.open(ReportVisibilityDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      disableClose: true,
      data: {
        healthRecordUid,
        clinicName: data?.clinicName,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result?.updated) {
          this.store.fetchRecord().subscribe();
        }
      });
  }

  canManageVisibility(property: any): boolean {
    const clinicUid = property?.clinicUid;
    if (!clinicUid) return false;

    const role = this.authService.getRoleFromToken();
    const currentClinicUid =
      role === 'ClinicOwner'
        ? this.selectClinicStore.selectedClinicUid()
        : this.authService.getDomainUid();

    return !!currentClinicUid && clinicUid === currentClinicUid;
  }

  openEditDialog(data: any) {
    const unpaid = Number(data?.owner?.unpaidExpenses ?? 0);
    const limit = Number(this.shellStore.debt()?.debtLimit ?? 0);

    const fmt0 = (v: number) =>
      new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(v);

    const hasLimit = Number.isFinite(limit) && limit > 0;
    const nearThreshold = hasLimit ? limit * 0.9 : Infinity;

    if (hasLimit && unpaid > limit) {
      const title = translate('debt.overLimit.title');
      const text = translate('debt.overLimit.text', {
        debt: fmt0(unpaid),
        limit: fmt0(limit),
      });

      const ref = this.dialog.open(ConfirmationDialogComponent, {
        width: '400px',
        data: { title, text },
        disableClose: true,
      });

      ref
        .afterClosed()
        .pipe(take(1))
        .subscribe((res) => {
          if (res?.confirmed) this.reportDialog(data);
        });
    } else if (hasLimit && unpaid >= nearThreshold && unpaid < limit) {
      const remaining = Math.max(0, limit - unpaid);
      const percent = Math.round((unpaid / limit) * 100);

      const title = translate('debt.nearLimit.title');
      const text = translate('debt.nearLimit.text', {
        debt: fmt0(unpaid),
        limit: fmt0(limit),
        remaining: fmt0(remaining),
        percent: String(percent),
      });

      const ref = this.dialog.open(ConfirmationDialogComponent, {
        width: '400px',
        data: { title, text },
        disableClose: true,
      });

      ref
        .afterClosed()
        .pipe(take(1))
        .subscribe((res) => {
          if (res?.confirmed) this.reportDialog(data);
        });
    } else {
      this.reportDialog(data);
    }
  }

  reportDialog(data?: any) {
    const dialogRef = this.dialog.open(AddEditReportDialogComponent, {
      ...PORTAL_DIALOG.wide,
      height: '85vh',
      disableClose: true,
      data: { ...data },
      autoFocus: false,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result?.success) {
          this.store.fetchRecord().subscribe();
        }
      });
  }

  previewImage(url: string) {
    const dialogRef = this.dialog.open(ImagePreviewDialogComponent, {
      data: { url },
      panelClass: 'image-dialog-panel',
      backdropClass: 'image-dialog-backdrop',
      hasBackdrop: true,
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      // if (result?.action === 'remove') {
      //   this.profileImage = null;
      //   this.uploadedFile = null;
      //   this.files = [];
      // } else if (result?.action === 'replace') {
      //   this.replaceImage();
      // }
    });
  }

  sendReportEmailToOwner(property: {
    healthRecordUid?: string;
    clinicUid?: string;
  }) {
    const healthRecordUid = property?.healthRecordUid;
    if (!healthRecordUid || !this.canManageVisibility(property)) return;
    if (this.sendingEmailUid() === healthRecordUid) return;

    const ref = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: translate('Send report to owner'),
        text: translate('Send the report PDF to the pet owner email address?'),
      },
      disableClose: true,
    });

    ref.afterClosed().pipe(take(1)).subscribe((result) => {
      if (!result?.confirmed) return;

      this.sendingEmailUid.set(healthRecordUid);
      this.healthRecordsService
        .healthRecordsUidReportEmailPost(
          healthRecordUid,
          this.transloco.getActiveLang(),
        )
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.sendingEmailUid.set(null);
            this.snackBar.open(
              translate('Report PDF sent to owner email.'),
              translate('Close'),
              { duration: 5000 },
            );
          },
          error: (err) => {
            this.sendingEmailUid.set(null);
            const message =
              err?.error?.detail ||
              err?.error?.title ||
              translate('Failed to send report email.');
            this.snackBar.open(message, translate('Close'), {
              duration: 7000,
              panelClass: ['error-snackbar'],
            });
          },
        });
    });
  }

  togglePreviewCard(healthRecordUid?: string, report?: { canView?: boolean; clinicName?: string }) {
    if (!healthRecordUid) {
      return;
    }

    if (report?.canView === false) {
      this.dialog.open(ConfirmationDialogComponent, {
        width: '480px',
        data: {
          title: translate('Report access restricted'),
          text: translate('report_access_restricted_message', {
            clinicName: report.clinicName ?? '',
          }),
        },
        disableClose: true,
      });
      return;
    }

    this.dialog.open(PreviewCardHistoryComponent, {
      ...PORTAL_DIALOG.wide,
      height: '85vh',
      data: { healthRecordUid },
    });
  }
}
